import DesktopMap from "@/components/AnimatedMap/DesktopMap"
import { Box, CardMedia, useMediaQuery } from "@mui/material"
import map_url from "@/assets/images/home/map.png"
import { useEffect, useMemo, useState } from "react"
import { desktopMapSizes, desktopSizes, mapStyles } from "./mockData"
import AnimatedCar from "@/components/AnimatedMap/Car"
import DesktopNotification from "./components/DesktopNotification"
import { getDesktopOptions, getNotificationOptions } from "./utils"
import { getSize } from "./utils"
import AnimatedMobileMap from "./components/AnimatedMobileMap"

const Map = () => {
  const [reset, setReset] = useState<number>(1);
  const isMobile = useMediaQuery("(max-width: 400px)");
  const isTablet = useMediaQuery("(max-width: 681px)");
  const isDesktop = useMediaQuery("(max-width: 1535px)");
  const isLargeDesktop = useMediaQuery("(max-width: 1679px)");
  const desktopMapSize = useMemo(
    () => {
      if (isTablet) {
        return desktopMapSizes.xs;
      }
      if (isLargeDesktop) {
        return desktopMapSizes.lg;
      }
      return desktopMapSizes.xl;
    },
    [isLargeDesktop, isTablet]
  );

  const desktopSize = useMemo(
    () => {
      if (isTablet) {
        return desktopSizes.xs;
      };
      if (isLargeDesktop) {
        return desktopSizes.lg;
      };
      return desktopSizes.xl;
    },
    [isLargeDesktop, isTablet]
  );

  const styles = useMemo(() => {
    if (isMobile) {
      return mapStyles.xs;
    };
    if (isTablet) {
      return mapStyles.sm;
    };
    if (isDesktop) {
      return mapStyles.md;
    };
    if (isLargeDesktop) {
      return mapStyles.lg;
    };
    return mapStyles.xl;
  }, [isDesktop, isTablet, isLargeDesktop, isMobile])

  const desktopOptions = useMemo(() => {
    const getWidth = getSize(desktopMapSizes.xl.width / desktopMapSize.width);
    const getHeight = getSize(desktopMapSizes.xl.height / desktopMapSize.height);
    return getDesktopOptions(getWidth, getHeight);
  }, [desktopMapSize]);

  const notificationOptions = useMemo(() => getNotificationOptions(desktopSize.width, isMobile), [desktopSize, isMobile]);

  const onChange = () => {
    return !document.hidden && setReset((prev) => prev + 1);
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", onChange);

    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  return (
    <Box 
      key={reset}
      sx={{ 
        top: "0px",
        ...styles.container
      }}
    >
      <DesktopMap
        sx={{
          maxWidth: desktopSize.width,
          maxHeight: desktopSize.height
        }}
        topBarSx={styles.topBar}
        leftSideSx={styles.leftSide}
        displaySearch={false}
      >
        <Box
          sx={{
            position: "absolute",
            width: desktopMapSize.width,
            height: desktopMapSize.height,
            ...desktopOptions.map
          }}
        >
          <CardMedia
            component="img"
            image={map_url}
            sx={{
              width: "100%",
              height: "100%"
            }}
          />
          {desktopOptions.cars.map((opt, index) => (
            <AnimatedCar
              key={index}
              options={opt}
              iconSx={{ fontSize: isTablet ? "8px" : isLargeDesktop ? "14px" : "20px" }}
            />
          ))}
        </Box>
        <Box
          sx={{
            position: "absolute",
            backgroundColor: "#8D99FF",
            opacity: "0.5",
            ...styles.fence
          }}
        />
        {notificationOptions.map((opt, index) => <DesktopNotification key={index} {...opt} />)}
      </DesktopMap>
      <AnimatedMobileMap />
    </Box>
  );
};

export default Map;
